import PropTypes from 'prop-types';
import { useState } from 'react';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// project imports
import MainCard from 'components/MainCard';
import { DebouncedInput } from 'components/third-party/react-table';

// ant design icons
import DownOutlined from '@ant-design/icons/DownOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';

// ==============================|| BEHAVIOR TRACKING TABLE ||============================== //

// Helper function to check if a date is today
const isToday = (dateString) => {
  const today = new Date();
  const checkDate = new Date(dateString);
  return (
    checkDate.getDate() === today.getDate() && checkDate.getMonth() === today.getMonth() && checkDate.getFullYear() === today.getFullYear()
  );
};

// Helper function to check if date is in the past
const isPastDate = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(dateString);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

// mm:ss (or a bare number of seconds) back to seconds.
//
// A bare number stays SECONDS, so the habit of typing 23 keeps working and means
// what it always did. Seconds are clamped at 59 rather than rejected: "2:75" is a
// slip, and silently refusing the keystroke leaves the person wondering why the
// field stopped accepting input.
const parseDuration = (text) => {
  const raw = String(text ?? '').trim();
  if (!raw) return 0;
  if (!raw.includes(':')) return parseInt(raw, 10) || 0;
  const [mins, secs] = raw.split(':');
  return (parseInt(mins, 10) || 0) * 60 + Math.min(59, parseInt(secs, 10) || 0);
};

// Helper function to format seconds as mm:ss
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

function BehaviorTrackingTable({ data, setData, behaviors, dataEntryMode = false }) {
  const intl = useIntl();
  // Literal text being typed into a duration cell, keyed by row. See the note on
  // parseDuration: without it the grid's re-derive reformats mid-keystroke.
  const [durationDraft, setDurationDraft] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRecord(null);
  };

  // Get behavior config by name
  const getBehaviorConfig = (behaviorName) => {
    return behaviors.find((b) => b.name === behaviorName);
  };

  // Calculate daily totals
  const calculateDailyTotals = (parent) => {
    if (!parent || !parent.subRows) return { count: 0, duration: 0 };

    const totalCount = parent.subRows.reduce((sum, row) => {
      const behaviorConfig = getBehaviorConfig(row.behavior);
      const hasFrequency = behaviorConfig?.tracking?.includes('frequency');
      return sum + (hasFrequency ? row.count || 0 : 0);
    }, 0);

    let totalSeconds = 0;
    parent.subRows.forEach((row) => {
      const behaviorConfig = getBehaviorConfig(row.behavior);
      const hasDuration = behaviorConfig?.tracking?.includes('duration');
      if (hasDuration) {
        totalSeconds += row.duration || 0;
      }
    });

    return { count: totalCount, duration: totalSeconds };
  };

  // Update behavior field
  const updateBehaviorField = (parentIndex, childIndex, field, value) => {
    setData((old) => {
      const newData = JSON.parse(JSON.stringify(old)); // Deep clone
      if (newData[parentIndex] && newData[parentIndex].subRows && newData[parentIndex].subRows[childIndex]) {
        newData[parentIndex].subRows[childIndex][field] = value;

        // Recalculate daily totals
        const totals = calculateDailyTotals(newData[parentIndex]);
        newData[parentIndex].count = totals.count;
        newData[parentIndex].duration = totals.duration;
      }
      return newData;
    });
  };

  // Add behavior row
  const addBehaviorRow = (parentIndex) => {
    setExpanded((old) => ({
      ...old,
      [parentIndex]: true
    }));

    setData((old) => {
      const newData = JSON.parse(JSON.stringify(old)); // Deep clone
      if (newData[parentIndex]) {
        if (!newData[parentIndex].subRows) {
          newData[parentIndex].subRows = [];
        }

        newData[parentIndex].subRows.push({
          behavior: '',
          function: '',
          count: 0,
          duration: 0
        });
      }
      return newData;
    });
  };

  // Delete behavior row
  const deleteBehaviorRow = (parentIndex, childIndex) => {
    setData((old) => {
      const newData = JSON.parse(JSON.stringify(old)); // Deep clone
      if (newData[parentIndex] && newData[parentIndex].subRows) {
        newData[parentIndex].subRows.splice(childIndex, 1);

        // Recalculate daily totals
        const totals = calculateDailyTotals(newData[parentIndex]);
        newData[parentIndex].count = totals.count;
        newData[parentIndex].duration = totals.duration;
      }
      return newData;
    });
  };

  // Filter data by date range
  const filteredData = data.filter((row) => {
    if (!startDate && !endDate) return true;

    const rowDate = new Date(row.date);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    rowDate.setHours(12, 0, 0, 0);

    if (start && end) {
      return rowDate >= start && rowDate <= end;
    } else if (start) {
      return rowDate >= start;
    } else if (end) {
      return rowDate <= end;
    }
    return true;
  });

  // Paginate data
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <MainCard content={false}>
        {/* Search and Filters - Only show in Historical mode */}
        {!dataEntryMode && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 2, alignItems: 'center' }}>
            <DebouncedInput
              value={globalFilter ?? ''}
              onFilterChange={(value) => setGlobalFilter(String(value))}
              placeholder={intl.formatMessage({ id: 'form.behaviorTracking.searchPlaceholder' })}
              sx={{ flexGrow: 1 }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={intl.formatMessage({ id: 'form.behaviorTracking.fromDate' })}
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 180 }
                  }
                }}
              />
              <DatePicker
                label={intl.formatMessage({ id: 'form.behaviorTracking.toDate' })}
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 180 }
                  }
                }}
              />
            </LocalizationProvider>
          </Stack>
        )}

        <TableContainer>
          <Table
            sx={{
              '& .MuiTableCell-root': {
                fontSize: '0.75rem',
                p: 1
              }
            }}
          >
            <TableHead>
              <TableRow>
                {dataEntryMode && <TableCell sx={{ width: 60 }}>{intl.formatMessage({ id: 'form.behaviorTracking.actions' })}</TableCell>}
                <TableCell sx={{ width: 60 }}></TableCell>
                <TableCell sx={{ width: 200 }}>{intl.formatMessage({ id: 'form.behaviorTracking.date' })}</TableCell>
                <TableCell sx={{ width: 250 }}>{intl.formatMessage({ id: 'form.behaviorTracking.behavior' })}</TableCell>
                <TableCell sx={{ width: 150 }}>{intl.formatMessage({ id: 'form.behaviorTracking.function' })}</TableCell>
                <TableCell sx={{ width: 120 }}>{intl.formatMessage({ id: 'form.behaviorTracking.count' })}</TableCell>
                <TableCell sx={{ width: 150 }}>{intl.formatMessage({ id: 'form.behaviorTracking.duration' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((parentRow, parentIndex) => {
                const isExpanded = expanded[parentIndex];
                const canEdit = !isPastDate(parentRow.date) && dataEntryMode;

                return (
                  <>
                    {/* Parent Row */}
                    <TableRow
                      key={`parent-${parentIndex}`}
                      sx={{
                        bgcolor: 'grey.50',
                        cursor: !dataEntryMode ? 'pointer' : 'default'
                      }}
                      onClick={
                        !dataEntryMode
                          ? (e) => {
                              if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'svg' && e.target.tagName !== 'path') {
                                handleViewRecord(parentRow);
                              }
                            }
                          : undefined
                      }
                    >
                      {dataEntryMode && (
                        <TableCell>
                          {canEdit && (
                            <Tooltip title={intl.formatMessage({ id: 'form.behaviorTracking.addNewBehavior' })}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addBehaviorRow(page * rowsPerPage + parentIndex);
                                }}
                              >
                                <PlusOutlined />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {parentRow.subRows && parentRow.subRows.length > 0 && (
                          <IconButton
                            size="small"
                            color={isExpanded ? 'primary' : 'default'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpanded((old) => ({
                                ...old,
                                [parentIndex]: !old[parentIndex]
                              }));
                            }}
                          >
                            {isExpanded ? <DownOutlined /> : <RightOutlined />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {parentRow.date}
                          </Typography>
                          {isToday(parentRow.date) && (
                            <Chip
                              label={intl.formatMessage({ id: 'form.behaviorTracking.today' })}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {intl.formatMessage({ id: 'form.behaviorTracking.dailySummary' })}
                        </Typography>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        <Chip label={parentRow.count || 0} size="small" color="primary" variant="filled" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDuration(parentRow.duration || 0)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Child Rows */}
                    {isExpanded &&
                      parentRow.subRows &&
                      parentRow.subRows.map((childRow, childIndex) => {
                        const behaviorConfig = getBehaviorConfig(childRow.behavior);
                        const hasFrequency = behaviorConfig?.tracking?.includes('frequency');
                        const hasDuration = behaviorConfig?.tracking?.includes('duration');

                        return (
                          <TableRow key={`child-${parentIndex}-${childIndex}`} sx={{ bgcolor: 'background.paper' }}>
                            {dataEntryMode && (
                              <TableCell>
                                {canEdit && (
                                  <Tooltip title={intl.formatMessage({ id: 'form.behaviorTracking.deleteBehavior' })}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteBehaviorRow(page * rowsPerPage + parentIndex, childIndex)}
                                    >
                                      <DeleteOutlined />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                            <TableCell></TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} sx={{ pl: 2 }}>
                                {hasFrequency && (
                                  <Chip
                                    label={intl.formatMessage({ id: 'form.label.frequency' })}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {hasDuration && (
                                  <Chip
                                    label={intl.formatMessage({ id: 'form.behaviorTracking.duration' })}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {canEdit ? (
                                <Select
                                  value={childRow.behavior || ''}
                                  onChange={(e) => {
                                    updateBehaviorField(page * rowsPerPage + parentIndex, childIndex, 'behavior', e.target.value);
                                  }}
                                  size="small"
                                  displayEmpty
                                  sx={{ minWidth: 200, fontSize: '0.75rem' }}
                                >
                                  <MenuItem value="">
                                    <em>{intl.formatMessage({ id: 'form.behaviorTracking.selectBehavior' })}</em>
                                  </MenuItem>
                                  {behaviors.map((behavior) => (
                                    <MenuItem key={behavior.id} value={behavior.name}>
                                      {behavior.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              ) : (
                                <Typography variant="body2" sx={{ pl: 2 }}>
                                  {childRow.behavior || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {canEdit ? (
                                <Select
                                  value={childRow.function || ''}
                                  onChange={(e) => {
                                    updateBehaviorField(page * rowsPerPage + parentIndex, childIndex, 'function', e.target.value);
                                  }}
                                  size="small"
                                  displayEmpty
                                  sx={{ minWidth: 120, fontSize: '0.75rem' }}
                                >
                                  <MenuItem value="">
                                    <em>{intl.formatMessage({ id: 'common.none' })}</em>
                                  </MenuItem>
                                  <MenuItem value="Attention">
                                    {intl.formatMessage({ id: 'form.behaviorTracking.functionAttention' })}
                                  </MenuItem>
                                  <MenuItem value="Auto">{intl.formatMessage({ id: 'form.behaviorTracking.functionAuto' })}</MenuItem>
                                  <MenuItem value="Escape">{intl.formatMessage({ id: 'form.behaviorTracking.functionEscape' })}</MenuItem>
                                </Select>
                              ) : (
                                <Typography variant="body2" sx={{ pl: 2 }}>
                                  {childRow.function || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {!hasFrequency ? (
                                <Typography variant="body2" color="text.disabled">
                                  {intl.formatMessage({ id: 'common.notAvailable' })}
                                </Typography>
                              ) : canEdit ? (
                                <TextField
                                  type="text"
                                  size="small"
                                  value={childRow.count ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    // Allow empty string or valid numbers
                                    if (val === '' || /^\d+$/.test(val)) {
                                      updateBehaviorField(
                                        page * rowsPerPage + parentIndex,
                                        childIndex,
                                        'count',
                                        val === '' ? '' : parseInt(val)
                                      );
                                    }
                                  }}
                                  onBlur={(e) => {
                                    // On blur, convert empty to 0
                                    if (e.target.value === '') {
                                      updateBehaviorField(page * rowsPerPage + parentIndex, childIndex, 'count', 0);
                                    }
                                  }}
                                  sx={{ width: 80 }}
                                />
                              ) : (
                                <Typography variant="body2">{childRow.count || 0}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {!hasDuration ? (
                                <Typography variant="body2" color="text.disabled">
                                  {intl.formatMessage({ id: 'common.notAvailable' })}
                                </Typography>
                              ) : canEdit ? (
                                <TextField
                                  type="text"
                                  size="small"
                                  placeholder={intl.formatMessage({ id: 'form.behaviorTracking.mmss' })}
                                  // The draft while this cell has focus, the canonical
                                  // mm:ss once it does not.
                                  value={durationDraft[`${parentIndex}-${childIndex}`] ?? formatDuration(childRow.duration || 0)}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    // Digits, with at most one colon and two seconds
                                    // digits after it. Anything else is not a keystroke
                                    // towards a valid duration, so it is ignored.
                                    if (val !== '' && !/^\d{0,4}(:\d{0,2})?$/.test(val)) return;
                                    setDurationDraft((d) => ({ ...d, [`${parentIndex}-${childIndex}`]: val }));
                                    updateBehaviorField(
                                      page * rowsPerPage + parentIndex,
                                      childIndex,
                                      'duration',
                                      val === '' ? '' : parseDuration(val)
                                    );
                                  }}
                                  onBlur={(e) => {
                                    // Commit and drop the draft, so the cell settles back
                                    // to mm:ss however it was typed.
                                    updateBehaviorField(
                                      page * rowsPerPage + parentIndex,
                                      childIndex,
                                      'duration',
                                      parseDuration(e.target.value)
                                    );
                                    setDurationDraft((d) => {
                                      const next = { ...d };
                                      delete next[`${parentIndex}-${childIndex}`];
                                      return next;
                                    });
                                  }}
                                  sx={{ width: 80 }}
                                />
                              ) : (
                                <Typography variant="body2">{formatDuration(childRow.duration || 0)}</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage(
              { id: 'form.behaviorTracking.showingEntries' },
              { from: page * rowsPerPage + 1, to: Math.min((page + 1) * rowsPerPage, filteredData.length), total: filteredData.length }
            )}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              size="small"
            >
              {[5, 10, 20, 50].map((pageSize) => (
                <MenuItem key={pageSize} value={pageSize}>
                  {intl.formatMessage({ id: 'form.behaviorTracking.showPageSize' }, { pageSize })}
                </MenuItem>
              ))}
            </Select>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, value) => {
                setPage(value - 1);
              }}
              color="primary"
              shape="rounded"
            />
          </Stack>
        </Box>
      </MainCard>

      {/* View Record Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {intl.formatMessage({ id: 'form.behaviorTracking.behaviorDetails' })} - {selectedRecord?.date}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Summary */}
            <Stack direction="row" spacing={2}>
              <Chip
                label={intl.formatMessage({ id: 'form.behaviorTracking.totalCount' }, { count: selectedRecord?.count || 0 })}
                color="primary"
              />
              <Chip
                label={intl.formatMessage(
                  { id: 'form.behaviorTracking.totalDuration' },
                  { duration: formatDuration(selectedRecord?.duration || 0) }
                )}
                color="error"
              />
            </Stack>

            {/* Behaviors List */}
            {selectedRecord?.subRows && selectedRecord.subRows.length > 0 ? (
              <Stack spacing={2}>
                <Typography variant="h6">{intl.formatMessage({ id: 'form.behaviorTracking.behaviors' })}</Typography>
                {selectedRecord.subRows.map((behavior, index) => (
                  <Paper key={index} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={600}>
                          {behavior.behavior}
                        </Typography>
                        {behavior.function && <Chip label={behavior.function} size="small" color="info" variant="outlined" />}
                      </Stack>
                      <Stack direction="row" spacing={3}>
                        {behavior.count !== undefined && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {intl.formatMessage({ id: 'form.behaviorTracking.countLabel' })}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {behavior.count}
                            </Typography>
                          </Stack>
                        )}
                        {behavior.duration !== undefined && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {intl.formatMessage({ id: 'form.behaviorTracking.durationLabel' })}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {behavior.duration}s
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'form.behaviorTracking.noBehaviorsRecorded' })}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>{intl.formatMessage({ id: 'common.close' })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

BehaviorTrackingTable.propTypes = {
  data: PropTypes.array.isRequired,
  setData: PropTypes.func.isRequired,
  behaviors: PropTypes.array.isRequired,
  dataEntryMode: PropTypes.bool
};

export default BehaviorTrackingTable;
